import React from "react";
import { Link } from "react-router-dom";
import Seo from "../../../components/public/Seo";
import "../../../styles/legal.css";

export default function TermsOfUse() {
  return (
    <>
      <Seo
        title="Terms of Use — Veya"
        description="The terms that apply when using Veya."
      />

      <main className="legal-page">
        <section className="legal-hero legal-hero--narrow">
          <p className="legal-kicker">Terms of Use</p>
          <h1>Terms of Use</h1>
          <p>
            These terms apply when you access or use Veya, including our
            shopping, styling, try-on, edit, bag, account and merchant-facing
            features.
          </p>
          <p className="legal-updated">Last updated: May 2026</p>
        </section>

        <article className="legal-document">
          <section>
            <h2>1. Acceptance of these terms</h2>
            <p>
              By accessing or using Veya, you agree to these Terms of Use. If
              you do not agree, you should not use Veya.
            </p>
            <p>
              Additional terms may apply to merchants, specific features,
              promotions, integrations or third-party services.
            </p>
          </section>

          <section>
            <h2>2. What Veya provides</h2>
            <p>
              Veya is a digital fashion experience platform. It helps shoppers
              discover products, build edits, save items, visualise garments,
              and explore boutique fashion with more intention.
            </p>
            <p>
              Veya may also provide merchants with catalogue tools, visibility,
              shopper interaction signals and style intelligence.
            </p>
          </section>

          <section>
            <h2>3. Accounts</h2>
            <p>
              Some features may require an account. You are responsible for
              keeping your login details secure and for activity that occurs
              under your account.
            </p>
            <p>
              You must provide accurate information and keep your details up to
              date. You must not create accounts using false, misleading or
              unauthorised information.
            </p>
          </section>

          <section>
            <h2>4. Shopper use</h2>
            <p>
              Shoppers may use Veya to browse products, save items, create
              edits, use try-on features, add items to a bag and explore
              boutique style inspiration.
            </p>
            <p>
              You must not misuse Veya, interfere with the platform, attempt to
              access another person’s account, scrape content without permission
              or use Veya for unlawful, harmful or misleading purposes.
            </p>
          </section>

          <section>
            <h2>5. Try-on disclaimer</h2>
            <p>
              Veya try-on is for visualisation only. Real fit will vary.
            </p>
            <p>
              Try-on results may not accurately reflect sizing, tailoring,
              fabric weight, stretch, drape, colour, texture, garment behaviour,
              lighting, body movement or how an item will feel when worn.
            </p>
            <p>
              Veya try-on should be treated as a styling and proportion guide,
              not a guarantee of fit, comfort, size, quality or suitability.
            </p>
          </section>

          <section>
            <h2>6. Product information</h2>
            <p>
              Product information may be supplied by merchants or other sources.
              This may include descriptions, prices, availability, images,
              sizing information, colours, materials and product links.
            </p>
            <p>
              We aim to display product information clearly, but we do not
              guarantee that all information will always be complete, accurate,
              current or error-free.
            </p>
            <p>
              Merchants are responsible for the accuracy of their own product
              information, pricing, availability, imagery, policies and legal
              disclosures.
            </p>
          </section>

          <section>
            <h2>7. Purchases, returns and merchant policies</h2>
            <p>
              Unless Veya clearly states otherwise, purchases are made with the
              relevant merchant, not directly with Veya.
            </p>
            <p>
              Merchant terms, shipping policies, return policies, refund
              policies, consumer guarantees and customer service processes may
              apply to purchases from that merchant.
            </p>
            <p>
              Veya may help shoppers discover or organise products, but we are
              not responsible for merchant fulfilment, stock availability,
              delivery, returns, refunds or after-sales service unless we
              expressly agree otherwise.
            </p>
          </section>

          <section>
            <h2>8. User content</h2>
            <p>
              You may upload, create, save or submit content through Veya,
              including edits, notes, product selections, images or account
              information.
            </p>
            <p>
              You remain responsible for content you provide. You must have the
              right to upload or submit it, and it must not infringe another
              person’s rights or breach any law.
            </p>
            <p>
              You give Veya permission to use, host, store, copy, process,
              display and analyse your content as reasonably needed to operate,
              improve and provide Veya’s features.
            </p>
          </section>

          <section>
            <h2>9. Veya content and intellectual property</h2>
            <p>
              Veya’s platform, branding, design, text, software, interface,
              styling systems, insights and original content are owned by or
              licensed to Veya.
            </p>
            <p>
              You may not copy, reproduce, modify, distribute, reverse engineer
              or commercially exploit Veya content or software except as allowed
              by law or with our written permission.
            </p>
          </section>

          <section>
            <h2>10. Third-party links and services</h2>
            <p>
              Veya may link to merchant websites, third-party platforms,
              payment services, analytics tools, hosting services, image
              services or other external services.
            </p>
            <p>
              We are not responsible for third-party websites, policies,
              products, services or content. You should review the relevant
              third-party terms and policies before using them.
            </p>
          </section>

          <section>
            <h2>11. Availability and changes</h2>
            <p>
              We may update, change, suspend or discontinue parts of Veya at any
              time. We may also correct errors, remove content, restrict access
              or change features where needed for security, legal, operational
              or product reasons.
            </p>
          </section>

          <section>
            <h2>12. Limitation of liability</h2>
            <p>
              To the extent permitted by law, Veya is not liable for indirect,
              consequential, special or incidental loss, loss of profit, loss of
              data, loss of opportunity, loss of goodwill, or loss arising from
              reliance on try-on results, product information, merchant content
              or third-party services.
            </p>
            <p>
              Nothing in these terms limits rights that cannot be excluded under
              applicable law.
            </p>
          </section>

          <section>
            <h2>13. Suspension or termination</h2>
            <p>
              We may suspend or terminate access to Veya if we believe these
              terms have been breached, if there is a security risk, if required
              by law, or if continued access may harm Veya, users, merchants or
              third parties.
            </p>
          </section>

          <section>
            <h2>14. Privacy</h2>
            <p>
              Our handling of personal information is explained in our{" "}
              <Link to="/legal/privacy">Privacy Policy</Link>.
            </p>
          </section>

          <section>
            <h2>15. Governing law</h2>
            <p>
              These terms are governed by the laws of New Zealand, unless a
              different law must apply under mandatory legal requirements.
            </p>
          </section>

          <section>
            <h2>16. Contact</h2>
            <p>
              For questions about these terms, contact{" "}
              <a href="mailto:hello@veya.co.nz">hello@veya.co.nz</a>.
            </p>
          </section>
        </article>
      </main>
    </>
  );
}